import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';
import type {
  View,
  ViewListItem,
  CreateViewRequest,
  UpdateViewRequest,
  CreateViewResponse,
  UpdateViewResponse,
  DeleteViewResponse,
  RefreshViewRequest,
  RefreshViewResponse,
  MaterializeViewRequest,
  MaterializeViewResponse,
  QueryViewResponse,
  ViewRefreshHistoryResponse,
  ViewFilesResponse,
  ViewListFilters,
  PaginationParams,
  PaginatedResponse,
  RulePreviewResult,
  PreviewColumnsRequest,
  PreviewColumnsResponse,
  ViewDependenciesResponse,
  ViewDependentsResponse,
  AffectedView,
  ViewFilter,
} from '@/types/views';

/**
 * Query keys for view-related queries
 * Centralized key factory for consistent cache management
 */
export const viewKeys = {
  all: ['views'] as const,
  lists: () => [...viewKeys.all, 'list'] as const,
  list: (params?: ViewListFilters & PaginationParams) => [...viewKeys.lists(), params] as const,
  details: () => [...viewKeys.all, 'detail'] as const,
  detail: (id: string) => [...viewKeys.details(), id] as const,
  data: (id: string, params?: { limit?: number; offset?: number; filters?: ViewFilter[] }) =>
    [...viewKeys.detail(id), 'data', params] as const,
  history: (id: string) => [...viewKeys.detail(id), 'history'] as const,
  files: (id: string) => [...viewKeys.detail(id), 'files'] as const,
  dependencies: (id: string) => [...viewKeys.detail(id), 'dependencies'] as const,
  dependents: (id: string) => [...viewKeys.detail(id), 'dependents'] as const,
};

/**
 * Hook to fetch paginated list of views
 *
 * Provides automatic caching, background refetching, and loading states
 * for view list data with optional filtering and pagination.
 *
 * Sends `include_stats: true` (#1663): the Views list page is where an operator
 * determines whether a view has data, so `file_count` and `total_rows` must be
 * populated, not the hard-coded 0 the backend returns when stats are not requested.
 * Unlike datasets (#964), the views stats query is bounded to the page's view IDs
 * (≤100 per page) rather than a full-catalog scan, so this is cheap.
 *
 * @param params - Optional query parameters
 * @param params.status - Filter by view status (active, building, failed, paused)
 * @param params.type - Filter by view type (virtual, materialized)
 * @param params.search - Search by name or description
 * @param params.tags - Filter by tags
 * @param params.is_auto_refresh - Filter by auto-refresh flag
 * @param params.page - Page number (1-indexed)
 * @param params.size - Number of items per page
 * @param params.sort_by - Field to sort by
 * @param params.sort_order - Sort direction (asc or desc)
 *
 * @example
 * ```tsx
 * function ViewList() {
 *   const { data, isLoading, error } = useViews({
 *     status: 'active',
 *     type: 'materialized',
 *     page: 1,
 *     size: 20,
 *     sort_by: 'last_refresh_at',
 *     sort_column_order: 'desc'
 *   });
 *
 *   if (isLoading) return <LoadingSpinner />;
 *   if (error) return <ErrorAlert error={error} />;
 *
 *   return (
 *     <div>
 *       {data.items.map(view => (
 *         <ViewCard key={view.id} view={view} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export const useViews = (params?: ViewListFilters & PaginationParams) => {
  return useQuery({
    queryKey: viewKeys.list(params),
    queryFn: async () => {
      const response = await apiClient.get<PaginatedResponse<ViewListItem>>('/views', {
        params: { ...params, include_stats: true },
      });
      return response.data;
    },
    staleTime: 300000, // Consider data fresh for 5 minutes
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook to fetch detailed information for a single view
 *
 * Automatically fetches view details including rules, columns, and transformations.
 *
 * @param id - View UUID
 * @param options - Optional query configuration
 * @param options.enabled - Whether to execute query (default: true if id provided)
 *
 * @example
 * ```tsx
 * function ViewDetails({ viewId }: { viewId: string }) {
 *   const { data: view, isLoading } = useView(viewId);
 *
 *   if (isLoading) return <Skeleton />;
 *
 *   return (
 *     <div>
 *       <h1>{view.name}</h1>
 *       <p>{view.description}</p>
 *       <RuleDisplay rules={view.rules} />
 *       <ColumnList columns={view.columns} />
 *     </div>
 *   );
 * }
 * ```
 */
export const useView = (id: string, options?: { enabled?: boolean; refetchInterval?: number | false }) => {
  return useQuery({
    queryKey: viewKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<View>(`/views/${id}`);
      return response.data;
    },
    enabled: options?.enabled !== false && !!id,
    staleTime: 300000, // Consider data fresh for 5 minutes
    refetchInterval: options?.refetchInterval,
  });
};

/**
 * Hook to query data from a view
 *
 * Executes the view query and returns the result set with pagination support.
 *
 * @param id - View UUID
 * @param params - Query parameters
 * @param params.limit - Number of rows to return
 * @param params.offset - Number of rows to skip
 * @param options - Optional query configuration
 * @param options.enabled - Whether to execute query (default: false, must be explicitly enabled)
 *
 * @example
 * ```tsx
 * function ViewDataPreview({ viewId }: { viewId: string }) {
 *   const [enabled, setEnabled] = useState(false);
 *   const { data, isLoading } = useViewData(viewId, { limit: 100 }, { enabled });
 *
 *   return (
 *     <div>
 *       <Button onClick={() => setEnabled(true)}>Query View</Button>
 *       {isLoading && <LoadingSpinner />}
 *       {data && <DataGrid columns={data.columns} rows={data.rows} />}
 *     </div>
 *   );
 * }
 * ```
 */
export const useViewData = (
  id: string,
  params?: { limit?: number; offset?: number; filters?: ViewFilter[] },
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: viewKeys.data(id, params),
    queryFn: async () => {
      const response = await apiClient.post<QueryViewResponse>(`/views/${id}/query`, {
        view_id: id,
        ...params,
      });
      return response.data;
    },
    enabled: options?.enabled === true && !!id,
    staleTime: 60000, // Consider data fresh for 1 minute
  });
};

/**
 * Hook to fetch refresh history for a view
 *
 * Returns a list of past refresh executions with timing and status information.
 *
 * @param id - View UUID
 * @param options - Optional query configuration
 * @param options.enabled - Whether to execute query (default: true if id provided)
 *
 * @example
 * ```tsx
 * function RefreshHistory({ viewId }: { viewId: string }) {
 *   const { data: history, isLoading } = useViewRefreshHistory(viewId);
 *
 *   if (isLoading) return <LoadingSpinner />;
 *
 *   return (
 *     <Table>
 *       {history.map(refresh => (
 *         <RefreshHistoryRow key={refresh.refresh_id} refresh={refresh} />
 *       ))}
 *     </Table>
 *   );
 * }
 * ```
 */
export const useViewRefreshHistory = (id: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: viewKeys.history(id),
    queryFn: async () => {
      // Backend returns { view_id, history: [...], limit, offset }
      const response = await apiClient.get<ViewRefreshHistoryResponse>(`/views/${id}/history`);
      return response.data.history;
    },
    enabled: options?.enabled !== false && !!id,
    staleTime: 60000, // Consider data fresh for 1 minute
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook to fetch files matched by a view
 *
 * Returns the list of parquet files that match the view's rules.
 *
 * @param id - View UUID
 * @param options - Optional query configuration
 * @param options.enabled - Whether to execute query (default: true if id provided)
 */
export const useViewFiles = (id: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: viewKeys.files(id),
    queryFn: async () => {
      const response = await apiClient.get<ViewFilesResponse>(`/views/${id}/files`);
      return response.data;
    },
    enabled: options?.enabled !== false && !!id,
    staleTime: 60000, // Consider data fresh for 1 minute
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook to create a new view
 *
 * Provides optimistic updates and automatic cache invalidation.
 * On success, invalidates view list queries to show the new view.
 *
 * @example
 * ```tsx
 * function CreateViewForm() {
 *   const createView = useCreateView();
 *
 *   const handleSubmit = async (data: CreateViewRequest) => {
 *     try {
 *       const response = await createView.mutateAsync(data);
 *       toast.success(`View "${response.view.name}" created successfully`);
 *       navigate(`/views/${response.view.id}`);
 *     } catch (error) {
 *       toast.error('Failed to create view');
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <Input name="name" label="View Name" required />
 *       <RuleBuilder />
 *       <Button loading={createView.isPending}>Create View</Button>
 *     </form>
 *   );
 * }
 * ```
 */
export const useCreateView = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateViewRequest) => {
      // Transform frontend rule format to backend format
      // Frontend uses: { type, config }
      // Backend expects: { rule_type, rule_config }
      const transformedRules = data.rules.map((rule, index) => ({
        rule_type: rule.type,
        rule_config: rule.config,
        logical_operator: data.rule_logic || 'AND',
        rule_order: index,
      }));

      // Transform column format to backend format
      // Frontend uses: { alias, transform, order }
      // Backend expects: { column_alias, transform_expression, column_order }
      // These are already correctly named, so we just pass them through
      const transformedColumns = data.columns?.map((col) => ({
        source_column: col.source_column,
        source_name: col.source_name,
        column_alias: col.column_alias,
        transform_expression: col.transform_expression,
        column_order: col.column_order,
      }));

      // Build the request payload with transformed data
      // Note: Joins don't need transformation - backend handles field aliases
      const requestPayload: any = {
        ...data,
        rules: transformedRules,
        columns: transformedColumns,
      };

      const response = await apiClient.post<CreateViewResponse>('/views', requestPayload, {
        timeout: 30000, // 30 seconds timeout for view creation
      });
      return response.data;
    },
    onSuccess: (newView) => {
      // Backend returns the View object directly
      // Invalidate all list queries to fetch updated data
      queryClient.invalidateQueries({ queryKey: viewKeys.lists() });

      // Optimistically add the new view to cache
      queryClient.setQueryData<View>(viewKeys.detail(newView.id), newView);
    },
  });
};

/**
 * Hook to update an existing view
 *
 * Provides optimistic updates for immediate UI feedback.
 * Automatically invalidates related queries on success.
 *
 * @example
 * ```tsx
 * function EditViewForm({ view }: { view: View }) {
 *   const updateView = useUpdateView();
 *
 *   const handleSubmit = async (data: UpdateViewRequest) => {
 *     try {
 *       await updateView.mutateAsync({ id: view.id, data });
 *       toast.success('View updated successfully');
 *     } catch (error) {
 *       toast.error('Failed to update view');
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <Input name="description" defaultValue={view.description} />
 *       <Button loading={updateView.isPending}>Save Changes</Button>
 *     </form>
 *   );
 * }
 * ```
 */
export const useUpdateView = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateViewRequest }) => {
      // Get current view data for version checking
      const currentView = queryClient.getQueryData<View>(viewKeys.detail(id));

      // Transform frontend rule format to backend format if rules are provided
      // Frontend uses: { type, config }
      // Backend expects: { rule_type, rule_config }
      let requestPayload: any = { ...data };
      if (data.rules && data.rules.length > 0) {
        const transformedRules = data.rules.map((rule, index) => ({
          rule_type: rule.type,
          rule_config: rule.config,
          logical_operator: data.rule_logic || 'AND',
          rule_order: index,
        }));
        requestPayload = {
          ...data,
          rules: transformedRules,
        };
      }

      // Add version information for conflict detection
      if (currentView?.updated_at) {
        requestPayload.expected_updated_at = currentView.updated_at;
      }

      // Note: Joins don't need transformation - backend handles field aliases

      const response = await apiClient.put<UpdateViewResponse>(`/views/${id}`, requestPayload, {
        timeout: 30000, // 30 seconds timeout for view updates
      });
      return response.data;
    },
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: viewKeys.detail(id) });

      // Snapshot previous value for rollback
      const previousView = queryClient.getQueryData<View>(viewKeys.detail(id));

      // Optimistically update cache
      if (previousView) {
        queryClient.setQueryData<View>(viewKeys.detail(id), {
          ...previousView,
          ...data,
          updated_at: new Date().toISOString(),
        });
      }

      return { previousView };
    },
    onError: (error: any, { id }, context) => {
      // Rollback on error
      if (context?.previousView) {
        queryClient.setQueryData(viewKeys.detail(id), context.previousView);
      }

      // Check for conflict errors (409 Conflict)
      if (error?.response?.status === 409) {
        // Invalidate cache to fetch latest version
        queryClient.invalidateQueries({ queryKey: viewKeys.detail(id) });

        // Re-throw with enhanced error message for UI handling
        throw new Error(
          'This view was modified by another user. Please refresh and try again.'
        );
      }
    },
    onSuccess: (updatedView, { id }) => {
      // Backend returns the View object directly
      // Update detail cache with server response
      queryClient.setQueryData(viewKeys.detail(id), updatedView);

      // Invalidate list queries to reflect changes
      queryClient.invalidateQueries({ queryKey: viewKeys.lists() });
    },
  });
};

/**
 * Hook to delete a view
 *
 * Removes view from cache on success and invalidates list queries.
 * WARNING: This will remove the view and all associated refresh history.
 * If materialized, it will also delete stored data. This cannot be undone.
 *
 * @example
 * ```tsx
 * function DeleteViewButton({ viewId }: { viewId: string }) {
 *   const deleteView = useDeleteView();
 *
 *   const handleDelete = async () => {
 *     if (!confirm('Are you sure? This will delete the view and cannot be undone.')) return;
 *
 *     try {
 *       await deleteView.mutateAsync(viewId);
 *       toast.success('View deleted successfully');
 *       navigate('/views');
 *     } catch (error) {
 *       toast.error('Failed to delete view');
 *     }
 *   };
 *
 *   return (
 *     <Button variant="destructive" onClick={handleDelete} loading={deleteView.isPending}>
 *       Delete View
 *     </Button>
 *   );
 * }
 * ```
 */
export const useDeleteView = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.delete<DeleteViewResponse>(`/views/${id}`, {
        timeout: 15000, // 15 seconds timeout for view deletion
      });
      return { id, ...response.data };
    },
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: viewKeys.detail(id) });

      // Snapshot previous value for rollback
      const previousView = queryClient.getQueryData<View>(viewKeys.detail(id));

      // Optimistically remove from cache
      queryClient.removeQueries({ queryKey: viewKeys.detail(id) });

      return { previousView };
    },
    onError: (_error, id, context) => {
      // Rollback on error
      if (context?.previousView) {
        queryClient.setQueryData(viewKeys.detail(id), context.previousView);
      }
    },
    onSuccess: (result) => {
      const deletedId = result.id;

      // Remove all related queries
      queryClient.removeQueries({ queryKey: viewKeys.detail(deletedId) });
      queryClient.removeQueries({ queryKey: viewKeys.data(deletedId) });
      queryClient.removeQueries({ queryKey: viewKeys.history(deletedId) });

      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: viewKeys.lists() });
    },
  });
};

/**
 * Hook to refresh a view
 *
 * Triggers a manual refresh of the view data, bypassing the schedule.
 * Use force flag to refresh even if recently refreshed.
 *
 * WARNING: This is a potentially long-running operation.
 * The request returns immediately with a refresh_id for tracking.
 *
 * @example
 * ```tsx
 * function RefreshViewButton({ viewId }: { viewId: string }) {
 *   const refreshView = useRefreshView();
 *
 *   const handleRefresh = async () => {
 *     try {
 *       const response = await refreshView.mutateAsync({
 *         view_id: viewId,
 *         force: true
 *       });
 *       toast.success('View refresh started');
 *     } catch (error) {
 *       toast.error('Failed to trigger refresh');
 *     }
 *   };
 *
 *   return (
 *     <Button onClick={handleRefresh} loading={refreshView.isPending}>
 *       Refresh View
 *     </Button>
 *   );
 * }
 * ```
 */
export const useRefreshView = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: RefreshViewRequest) => {
      const response = await apiClient.post<RefreshViewResponse>(
        `/views/${request.view_id}/refresh`,
        request,
        {
          timeout: 60000, // 60 seconds timeout for refresh operations
        }
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      const viewId = variables.view_id;

      // Invalidate view details to show updated refresh status
      queryClient.invalidateQueries({ queryKey: viewKeys.detail(viewId) });

      // Invalidate refresh history
      queryClient.invalidateQueries({ queryKey: viewKeys.history(viewId) });

      // Update last_refresh_at optimistically
      const existingView = queryClient.getQueryData<View>(viewKeys.detail(viewId));
      if (existingView) {
        queryClient.setQueryData<View>(viewKeys.detail(viewId), {
          ...existingView,
          status: 'building',
          last_refresh_at: new Date().toISOString(),
        });
      }
    },
  });
};

/**
 * Hook to materialize a view
 *
 * Converts a virtual view to a materialized view by pre-computing
 * and storing results as Parquet files.
 *
 * WARNING: This is a long-running operation that may take several minutes.
 *
 * @example
 * ```tsx
 * function MaterializeViewButton({ viewId }: { viewId: string }) {
 *   const materializeView = useMaterializeView();
 *
 *   const handleMaterialize = async () => {
 *     if (!confirm('This will create a materialized copy of the view. Continue?')) return;
 *
 *     try {
 *       const response = await materializeView.mutateAsync({ view_id: viewId });
 *       toast.success('View materialized successfully');
 *     } catch (error) {
 *       toast.error('Failed to materialize view');
 *     }
 *   };
 *
 *   return (
 *     <Button onClick={handleMaterialize} loading={materializeView.isPending}>
 *       Materialize View
 *     </Button>
 *   );
 * }
 * ```
 */
export const useMaterializeView = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: MaterializeViewRequest) => {
      const response = await apiClient.post<MaterializeViewResponse>(
        `/views/${request.view_id}/materialize`,
        request,
        {
          timeout: 300000, // 5 minutes timeout for materialization
        }
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      const viewId = variables.view_id;

      // Invalidate view details to show updated type and storage location
      queryClient.invalidateQueries({ queryKey: viewKeys.detail(viewId) });

      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: viewKeys.lists() });
    },
  });
};

/**
 * Hook to dematerialize a view
 *
 * Converts a materialized view back to a virtual view by removing
 * the stored Parquet files.
 *
 * @example
 * ```tsx
 * function DematerializeViewButton({ viewId }: { viewId: string }) {
 *   const dematerializeView = useDematerializeView();
 *
 *   const handleDematerialize = async () => {
 *     if (!confirm('This will delete the stored data. Continue?')) return;
 *
 *     try {
 *       await dematerializeView.mutateAsync(viewId);
 *       toast.success('View dematerialized successfully');
 *     } catch (error) {
 *       toast.error('Failed to dematerialize view');
 *     }
 *   };
 *
 *   return (
 *     <Button onClick={handleDematerialize} loading={dematerializeView.isPending}>
 *       Convert to Virtual
 *     </Button>
 *   );
 * }
 * ```
 */
export const useDematerializeView = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (viewId: string) => {
      const response = await apiClient.delete<DeleteViewResponse>(
        `/views/${viewId}/materialize`
      );
      return { viewId, ...response.data };
    },
    onSuccess: (result) => {
      const viewId = result.viewId;

      // Invalidate view details to show updated type
      queryClient.invalidateQueries({ queryKey: viewKeys.detail(viewId) });

      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: viewKeys.lists() });
    },
  });
};

/**
 * Hook to preview rule matching results
 *
 * Returns information about how many files match the given rules
 * without creating the view.
 *
 * @example
 * ```tsx
 * function RulePreview({ rules }: { rules: ViewRule[] }) {
 *   const previewRules = usePreviewRules();
 *   const [preview, setPreview] = useState<RulePreviewResult | null>(null);
 *
 *   const handlePreview = async () => {
 *     try {
 *       const result = await previewRules.mutateAsync({ rules });
 *       setPreview(result);
 *     } catch (error) {
 *       toast.error('Failed to preview rules');
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       <Button onClick={handlePreview} loading={previewRules.isPending}>
 *         Preview Rules
 *       </Button>
 *       {preview && <PreviewResults result={preview} />}
 *     </div>
 *   );
 * }
 * ```
 */
export const usePreviewRules = () => {
  return useMutation({
    mutationFn: async (request: { rules: any[]; rule_logic?: 'AND' | 'OR' }) => {
      // Transform frontend rule format to backend format
      // Frontend uses: { type, config }
      // Backend expects: { rule_type, rule_config }
      const transformedRules = request.rules.map((rule) => ({
        rule_type: rule.type,
        rule_config: rule.config,
        logical_operator: request.rule_logic || 'AND',
        rule_column_order: 0,
      }));

      const response = await apiClient.post<RulePreviewResult>('/views/preview-rules', {
        rules: transformedRules,
        rule_logic: request.rule_logic,
      }, {
        timeout: 30000, // 30 seconds timeout for rule preview
      });
      return response.data;
    },
    // No cache management needed - preview results are ephemeral
  });
};

/**
 * Hook to preview available columns for a view configuration
 *
 * Returns columns that would be available based on the rules and joins
 * without creating the view. Used in the Column Configuration step.
 *
 * @example
 * ```tsx
 * function ColumnPreview({ rules, joins }: { rules: ViewRule[], joins: JoinConfig[] }) {
 *   const previewColumns = usePreviewColumns();
 *   const [preview, setPreview] = useState<PreviewColumnsResponse | null>(null);
 *
 *   const handlePreview = async () => {
 *     try {
 *       const result = await previewColumns.mutateAsync({ rules, joins });
 *       setPreview(result);
 *     } catch (error) {
 *       toast.error('Failed to preview columns');
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       <Button onClick={handlePreview} loading={previewColumns.isPending}>
 *         Preview Columns
 *       </Button>
 *       {preview && <ColumnCheckboxList columns={preview.columns} />}
 *     </div>
 *   );
 * }
 * ```
 */
export const usePreviewColumns = () => {
  return useMutation({
    mutationFn: async (request: PreviewColumnsRequest) => {
      // Transform frontend rule format to backend format
      // Frontend uses: { type, config }
      // Backend expects: { rule_type, rule_config }
      const transformedRules = request.rules.map((rule, index) => ({
        rule_type: rule.type,
        rule_config: rule.config,
        logical_operator: request.rule_logic || 'AND',
        rule_order: index,
      }));

      // Transform joins if present
      // Backend expects the joins in the same format they're stored
      const requestPayload: any = {
        rules: transformedRules,
        rule_logic: request.rule_logic || 'AND',
      };

      if (request.joins && request.joins.length > 0) {
        requestPayload.joins = request.joins;
      }

      const response = await apiClient.post<PreviewColumnsResponse>('/views/preview-columns', requestPayload, {
        timeout: 30000, // 30 seconds timeout for column preview
      });
      return response.data;
    },
    // No cache management needed - preview results are ephemeral
  });
};

/**
 * Hook to fetch views that this view depends on
 *
 * Returns views referenced in JOINs with source_type='view'.
 *
 * @param viewId - View UUID
 * @param options - Optional query configuration
 * @param options.enabled - Whether to execute query (default: true if viewId provided)
 *
 * @example
 * ```tsx
 * function ViewDependencies({ viewId }: { viewId: string }) {
 *   const { data, isLoading } = useViewDependencies(viewId);
 *
 *   if (isLoading) return <LoadingSpinner />;
 *
 *   return (
 *     <div>
 *       <h3>This view depends on:</h3>
 *       {data.dependencies.map(dep => (
 *         <DependencyItem key={dep.view_id} dependency={dep} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export const useViewDependencies = (viewId: string | undefined, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: viewKeys.dependencies(viewId!),
    queryFn: async () => {
      const response = await apiClient.get<ViewDependenciesResponse>(`/views/${viewId}/dependencies`);
      return response.data;
    },
    enabled: options?.enabled !== false && !!viewId,
    staleTime: 300000, // Consider data fresh for 5 minutes
  });
};

/**
 * Hook to fetch views that depend on this view
 *
 * Returns views that JOIN to this view.
 *
 * @param viewId - View UUID
 * @param options - Optional query configuration
 * @param options.enabled - Whether to execute query (default: true if viewId provided)
 *
 * @example
 * ```tsx
 * function ViewDependents({ viewId }: { viewId: string }) {
 *   const { data, isLoading } = useViewDependents(viewId);
 *
 *   if (isLoading) return <LoadingSpinner />;
 *
 *   return (
 *     <div>
 *       <h3>Views that depend on this:</h3>
 *       {data.dependents.map(dep => (
 *         <DependentItem key={dep.view_id} dependent={dep} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export const useViewDependents = (viewId: string | undefined, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: viewKeys.dependents(viewId!),
    queryFn: async () => {
      const response = await apiClient.get<ViewDependentsResponse>(`/views/${viewId}/dependents`);
      return response.data;
    },
    enabled: options?.enabled !== false && !!viewId,
    staleTime: 300000, // Consider data fresh for 5 minutes
  });
};

/**
 * Hook to fetch all affected views (both dependencies and dependents)
 *
 * Useful for impact analysis when editing or deleting a view.
 * Combines both upstream dependencies and downstream dependents.
 *
 * @param viewId - View UUID
 * @param options - Optional query configuration
 * @param options.enabled - Whether to execute query (default: true if viewId provided)
 *
 * @example
 * ```tsx
 * function AffectedViewsWarning({ viewId }: { viewId: string }) {
 *   const { data: affected, isLoading } = useAffectedViews(viewId);
 *
 *   if (isLoading) return <LoadingSpinner />;
 *   if (!affected || affected.length === 0) return null;
 *
 *   return (
 *     <Alert>
 *       <AlertTitle>Impact Warning</AlertTitle>
 *       <AlertDescription>
 *         Changes will affect {affected.length} other views
 *       </AlertDescription>
 *     </Alert>
 *   );
 * }
 * ```
 */
export const useAffectedViews = (viewId: string | undefined, options?: { enabled?: boolean }) => {
  const { data: dependents } = useViewDependents(viewId, options);

  // Transform dependents to AffectedView format
  // For now, we only care about dependents (views that would break if we change/delete this view)
  // Dependencies are what this view needs, but changing them doesn't directly affect this view
  const affectedViews: AffectedView[] = dependents?.dependents.map(dep => ({
    view_id: dep.view_id,
    view_name: dep.view_name,
    impact_type: 'direct' as const,
  })) || [];

  return {
    data: affectedViews,
    isLoading: !dependents,
  };
};
